import type { GeoMarkupStore } from '../index';
import type { Note, NoteListItem, NoteMapItem } from '@/types/notes';
import { extractUniqueSortedStrings } from '@/utils/helpers';
import { dateToISOString } from '@/utils/dateHelpers';

// ===== ATOMIC NOTE STATE SELECTORS =====

/**
 * Select all notes from the store
 */
export const selectNotes = (state: GeoMarkupStore): NoteListItem[] =>
  state.notes.items;

/**
 * Select note map items (lightweight markers for map display)
 */
export const selectMapNotes = (state: GeoMarkupStore) => state.notes.mapNotes;

/**
 * Select currently selected note ID
 */
export const selectNotesSelectedId = (state: GeoMarkupStore): string | null =>
  state.notes.selectedId;

/**
 * Select notes loading state
 */
export const selectNotesIsLoading = (state: GeoMarkupStore): boolean =>
  state.notes.isLoading;

/**
 * Whether the notes list has completed at least one fetch attempt.
 */
export const selectNotesHasLoaded = (state: GeoMarkupStore): boolean =>
  state.notes.hasLoaded;

/**
 * Select notes syncing state (for individual note operations)
 */
export const selectNotesIsSyncing = (state: GeoMarkupStore): boolean =>
  state.notes.isSyncing;

/**
 * Select notes error state
 */
export const selectNotesError = (state: GeoMarkupStore) => state.notes.error;

// ===== DERIVED NOTE SELECTORS =====

/**
 * Select the currently selected note by matching selectedId against items.
 * Returns NoteListItem for list display. Full Note details should be loaded via fetchNoteById.
 */
export const selectSelectedNote = (
  state: GeoMarkupStore
): NoteListItem | null => {
  const { items, selectedId } = state.notes;
  if (!selectedId) return null;
  return items.find((note) => note.id === selectedId) ?? null;
};

/**
 * Select the detailed (full) note loaded via fetchNoteById.
 * Contains content, attachments[], and linkedTaskIds[] unlike NoteListItem.
 */
export const selectDetailedNote = (state: GeoMarkupStore): Note | null =>
  state.notes.detailedNote;

/**
 * Select whether there are more notes pages to load
 */
export const selectNotesHasNextPage = (state: GeoMarkupStore): boolean =>
  state.notes.hasNextPage;

/**
 * Select the total number of notes matching current filters
 */
export const selectNotesTotalCount = (state: GeoMarkupStore): number =>
  state.notes.totalCount;

// ===== NOTE UI SELECTORS =====

/**
 * Select whether the note form is open.
 */
export const selectIsNoteFormOpen = (state: GeoMarkupStore): boolean =>
  state.notes.formIsOpen;

/**
 * Select the note form mode (`create`, `edit`, or `null`).
 */
export const selectNoteFormMode = (
  state: GeoMarkupStore
): 'create' | 'edit' | null => state.notes.formMode;

/**
 * Select whether the note details panel is open.
 */
export const selectIsNoteDetailOpen = (state: GeoMarkupStore): boolean =>
  state.notes.detailsIsOpen;

// ===== NOTE FILTER SELECTORS =====

/**
 * Select all note filter state
 */
export const selectNoteFilters = (state: GeoMarkupStore) => state.noteFilters;

/**
 * Select note search text filter
 */
export const selectNoteFilterSearch = (state: GeoMarkupStore): string =>
  state.noteFilters.search;

/**
 * Check if any note filters are active (excluding search and pagination/sort)
 */
export const selectHasActiveNoteFilters = (state: GeoMarkupStore): boolean => {
  const {
    search,
    visibility,
    colors,
    authors,
    dateFrom,
    dateTo,
    hasLocation,
    location,
  } = state.noteFilters;
  return !!(
    search ||
    visibility !== null ||
    colors.length > 0 ||
    authors.length > 0 ||
    dateFrom !== null ||
    dateTo !== null ||
    hasLocation !== null ||
    location
  );
};

/**
 * Excludes `search` because on the map view the SearchBar lives OUTSIDE the
 * collapsible Filters panel — the user already sees it, so it must not light
 * up the filters-dot on the toggle button.
 */
export const selectHasActiveNoteFiltersInMapPanel = (
  state: GeoMarkupStore
): boolean => {
  const {
    visibility,
    colors,
    authors,
    dateFrom,
    dateTo,
    hasLocation,
    location,
  } = state.noteFilters;
  return !!(
    visibility !== null ||
    colors.length > 0 ||
    authors.length > 0 ||
    dateFrom !== null ||
    dateTo !== null ||
    hasLocation !== null ||
    location
  );
};

/**
 * Serialized key of note filter state (excluding search, pagination).
 * Changes on every individual filter mutation — suitable as a useEffect dependency.
 */
export const selectNoteFilterKey = (state: GeoMarkupStore): string => {
  const {
    visibility,
    colors,
    authors,
    dateFrom,
    dateTo,
    hasLocation,
    location,
    sortBy,
    sortDescending,
  } = state.noteFilters;
  return `${visibility}|${colors.join(',')}|${authors.join(',')}|${dateFrom?.getTime() ?? ''}|${dateTo?.getTime() ?? ''}|${hasLocation}|${location}|${sortBy}|${sortDescending}`;
};

/**
 * Check if any advanced note filters are active (excluding visibility, search, sort).
 * Used by FilterIconButton to show the active-filters dot.
 */
export const selectHasAdvancedNoteFilters = (
  state: GeoMarkupStore
): boolean => {
  const { colors, authors, dateFrom, dateTo, hasLocation, location } =
    state.noteFilters;
  return !!(
    colors.length > 0 ||
    authors.length > 0 ||
    dateFrom !== null ||
    dateTo !== null ||
    hasLocation !== null ||
    location
  );
};

/**
 * Used on the list view where the SearchBar lives INSIDE the collapsible
 * Filters panel — so a non-empty search must light the filter-button dot.
 * Visibility stays excluded because visibility tabs render separately in
 * the header.
 */
export const selectHasActiveNoteFiltersInListPanel = (
  state: GeoMarkupStore
): boolean => selectHasAdvancedNoteFilters(state) || !!state.noteFilters.search;

/**
 * Count the number of active advanced note filter categories (excluding visibility).
 * Each filter type counts as 1 regardless of how many values are selected.
 */
export const selectActiveNoteFilterCount = (state: GeoMarkupStore): number => {
  const { colors, authors, dateFrom, dateTo, hasLocation, location } =
    state.noteFilters;
  let count = 0;
  if (colors.length > 0) count++;
  if (authors.length > 0) count++;
  if (dateFrom !== null || dateTo !== null) count++;
  if (hasLocation !== null || location) count++;
  return count;
};

/**
 * Select unique location values from all notes that have a location.
 * Returns a sorted array of distinct non-empty location strings.
 * Memoized: only recomputes when notes.items reference changes.
 */
let cachedItems: NoteListItem[] | null = null;
let cachedLocations: string[] = [];

export const selectUniqueNoteLocations = (state: GeoMarkupStore): string[] => {
  if (state.notes.items === cachedItems) return cachedLocations;
  cachedItems = state.notes.items;
  cachedLocations = extractUniqueSortedStrings(
    state.notes.items,
    (note) => note.location
  );
  return cachedLocations;
};

// ===== FILTERED MAP SELECTORS =====

/**
 * Module-level memoization cache for selectFilteredMapNotes.
 * Only recomputes when mapNotes reference or filter key changes.
 */
let cachedMapNotes: NoteMapItem[] | null = null;
let cachedNoteFilterKey: string = '';
let cachedFilteredMapNotes: NoteMapItem[] = [];

/**
 * Select map notes filtered by the active note filter state.
 * Applies all 8 filter fields (search, visibility, colors, author, dateFrom, dateTo, hasLocation, location)
 * client-side to the mapNotes array.
 *
 * Memoized: only recomputes when mapNotes reference or filter key changes.
 */
export const selectFilteredMapNotes = (
  state: GeoMarkupStore
): NoteMapItem[] => {
  const mapNotes = state.notes.mapNotes;
  const {
    search,
    visibility,
    colors,
    authors,
    dateFrom,
    dateTo,
    hasLocation,
    location,
  } = state.noteFilters;

  // Build a cache key from the raw filter fields
  const filterKey = `${search}|${visibility ?? ''}|${colors.join(',')}|${authors.join(',')}|${dateFrom?.getTime() ?? ''}|${dateTo?.getTime() ?? ''}|${hasLocation}|${location}`;

  if (mapNotes === cachedMapNotes && filterKey === cachedNoteFilterKey) {
    return cachedFilteredMapNotes;
  }

  cachedMapNotes = mapNotes;
  cachedNoteFilterKey = filterKey;

  // If no filters are active, return the full array
  if (!selectHasActiveNoteFilters(state)) {
    cachedFilteredMapNotes = mapNotes;
    return cachedFilteredMapNotes;
  }

  const searchLower = search.toLowerCase();
  const dateFromStr = dateFrom ? dateToISOString(dateFrom) : null;
  const dateToStr = dateTo ? dateToISOString(dateTo) : null;
  const locationLower = location.toLowerCase();

  cachedFilteredMapNotes = mapNotes.filter((note) => {
    // Search: case-insensitive match on title
    if (searchLower && !note.title.toLowerCase().includes(searchLower)) {
      return false;
    }

    // Visibility: exact match
    if (visibility !== null && note.visibility !== visibility) {
      return false;
    }

    // Colors: note.color must be in the colors array
    if (colors.length > 0 && !colors.includes(note.color)) {
      return false;
    }

    // Author: note.authorId must be in the selected authors array
    if (authors.length > 0 && !authors.includes(note.authorId)) {
      return false;
    }

    // Date range: day-level comparison against note.createdAt
    if (dateFromStr || dateToStr) {
      const noteDateStr = note.createdAt.slice(0, 10);
      if (dateFromStr && noteDateStr < dateFromStr) return false;
      if (dateToStr && noteDateStr > dateToStr) return false;
    }

    // hasLocation ignored: all map items have coordinates by definition

    // Location: case-insensitive includes
    if (locationLower) {
      const noteLocation = note.location?.toLowerCase() ?? '';
      if (!noteLocation.includes(locationLower)) return false;
    }

    return true;
  });

  return cachedFilteredMapNotes;
};
